import React, { useState, useEffect, useCallback } from 'react';
import Web3Service, {
  ContractStatus,
  AutomationWalletStatus,
  truncateDecimals,
  formatWithCommas,
  EXPLORER_URL,
  VESTING_ADDRESS,
} from '../services/Web3Services';
import AdminDashboard from './AdminDashboard';
import AutomationWalletPanel from './AutomationWalletPanel';
import NoticePanel from './NoticePanel';
import { fetchVisibleNotices } from '../services/NoticeService';

interface AdminPageProps {
  account: string;
}

// ── Design tokens ─────────────────────────────────────────────────────────
const INK = '#F2F7F4';
const MUTED = '#8A9992';
const DIM = '#7A8880';
const SOFT = '#D6E2DC';
const MINT = '#5EE9B5';
const GREEN = '#34D399';
const AMBER = '#F5B44C';
const RED = '#F0616B';
/** Softer terracotta for destructive-but-routine cards (Withdraw CVT, pause). */
const EMBER = '#E08A5C';

const CARD: React.CSSProperties = {
  background: 'linear-gradient(160deg, rgba(52,211,153,0.07), rgba(255,255,255,0.025) 55%)',
  border: '1px solid rgba(94,233,181,0.16)',
  borderRadius: 14,
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

/**
 * Destructive cards. Deliberately restrained: a saturated crimson wash reads as
 * an error state rather than "handle with care", and next to the green page it
 * fought for attention. The signal now comes from the icon, the button outline
 * and the confirm gate, with the card only faintly tinted.
 */
const CARD_DANGER: React.CSSProperties = {
  ...CARD,
  background: 'linear-gradient(160deg, rgba(224,138,92,0.055), rgba(255,255,255,0.02) 55%)',
  border: '1px solid rgba(224,138,92,0.22)',
};

const INPUT: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '9px 11px',
  color: INK,
  fontSize: 14,
  fontFamily: "'Sora',sans-serif",
  outline: 'none',
};

const BTN_BASE: React.CSSProperties = {
  borderRadius: 9,
  padding: 11,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "'Sora',sans-serif",
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  ...BTN_BASE,
  border: 0,
  background: 'linear-gradient(180deg, #19B37C, #0E8A5C)',
  color: '#03150D',
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const btnOutline = (disabled: boolean, tone: string = 'rgba(255,255,255,0.14)', color: string = SOFT): React.CSSProperties => ({
  ...BTN_BASE,
  border: `1px solid ${tone}`,
  background: 'rgba(255,255,255,0.03)',
  color,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

/**
 * Small pill button used across the status strip (Calculate/Refresh, Manage,
 * the users link). One shared style so they read as the same kind of control
 * rather than three different affordances.
 */
const chipBtn = (disabled = false): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  border: `1px solid ${MINT}40`,
  background: `${MINT}14`,
  color: MINT,
  borderRadius: 999,
  padding: '4px 10px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: "'Sora',sans-serif",
  fontSize: 11,
  fontWeight: 600,
  opacity: disabled ? 0.5 : 1,
  whiteSpace: 'nowrap',
});

/**
 * Shared refresh control for the on-chain figures in the status strip.
 *
 * Deliberately muted (~30% softer than chipBtn): it sits beside a large number
 * and would otherwise compete with it. One handler behind all three cells so
 * fees, balances and claimable can never drift out of sync with each other.
 */
const RefreshGlyph: React.FC<{
  onClick: () => void;
  busy: boolean;
  label: string;
}> = ({ onClick, busy, label }) => (
  <button
    onClick={onClick}
    disabled={busy}
    title={busy ? 'Refreshing…' : label}
    aria-label={label}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 24,
      height: 24,
      flex: 'none',
      borderRadius: 999,
      border: `1px solid ${MINT}2D`,
      background: `${MINT}0E`,
      color: MINT,
      cursor: busy ? 'not-allowed' : 'pointer',
      opacity: busy ? 0.5 : 0.7,
      padding: 0,
    }}
  >
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={busy ? { animation: 'spin 0.9s linear infinite' } : undefined}
    >
      <path d="M20 11A8 8 0 006.3 5.7L2 10" />
      <path d="M4 13a8 8 0 0013.7 5.3L22 14" />
      <path d="M2 4v6h6M22 20v-6h-6" />
    </svg>
  </button>
);

const LABEL: React.CSSProperties = {
  fontSize: 11,
  color: DIM,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

const Spinner = () => (
  <span
    style={{
      width: 14,
      height: 14,
      borderRadius: 999,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'currentColor',
      display: 'inline-block',
      animation: 'spin 0.7s linear infinite',
    }}
  />
);

/** Small coloured square that carries each card's icon. */
const IconBox: React.FC<{ tone: string; children: React.ReactNode }> = ({ tone, children }) => (
  <div
    style={{
      width: 28,
      height: 28,
      borderRadius: 8,
      background: `${tone}1F`,
      border: `1px solid ${tone}44`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 'none',
      color: tone,
    }}
  >
    {children}
  </div>
);

/**
 * Full contract address, click to copy. The header pills truncate it, but an
 * admin pasting into a explorer or wallet needs the whole thing.
 */
const CopyableAddress: React.FC<{ address: string }> = ({ address }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(address);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      title={copied ? 'Copied' : 'Click to copy'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        marginTop: 10,
        padding: '7px 8px 7px 14px',
        border: '1px solid rgba(255,255,255,0.09)',
        // Matches the CONTRACT / OWNER chips above it.
        borderRadius: 999,
        background: 'rgba(255,255,255,0.03)',
        cursor: 'pointer',
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: 12,
        color: SOFT,
        textAlign: 'left',
      }}
    >
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{address}</span>
      <span
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: 999,
          color: copied ? MINT : MUTED,
        }}
      >
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V5a2 2 0 012-2h10" />
          </svg>
        )}
      </span>
    </button>
  );
};

const SectionHeading: React.FC<{ title: string; note: string; tone?: string }> = ({ title, note, tone }) => (
  <div style={{ margin: '32px 0 14px' }}>
    <div style={{ ...LABEL, color: tone || MINT }}>{title}</div>
    <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>{note}</div>
  </div>
);

/** Per-card result line. Keeps feedback next to the action that caused it. */
const Result: React.FC<{ result?: { ok: boolean; text: string } }> = ({ result }) => {
  if (!result) return null;
  return (
    <div
      style={{
        fontSize: 12,
        color: result.ok ? MINT : RED,
        background: result.ok ? 'rgba(94,233,181,0.08)' : 'rgba(240,97,107,0.08)',
        border: `1px solid ${result.ok ? 'rgba(94,233,181,0.25)' : 'rgba(240,97,107,0.25)'}`,
        borderRadius: 8,
        padding: '8px 10px',
      }}
    >
      {result.text}
    </div>
  );
};

type Results = Record<string, { ok: boolean; text: string } | undefined>;

interface PendingConfirm {
  title: string;
  body: string;
  word: string;
  cta: string;
  run: () => Promise<void>;
}

const AdminPage: React.FC<AdminPageProps> = ({ account }) => {
  const [user, setUser] = useState('');
  const [globalInitialPercent, setGlobalInitialPercent] = useState('');
  const [globalMinutelyPercent, setGlobalMinutelyPercent] = useState('');
  const [recoverTokenAddress, setRecoverTokenAddress] = useState('');
  const [adminAddress, setAdminAddress] = useState('');
  const [emergencyClaimUser, setEmergencyClaimUser] = useState('');

  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [results, setResults] = useState<Results>({});
  const [showDashboard, setShowDashboard] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  const [showNotices, setShowNotices] = useState(false);
  /** Count of notices visitors can currently see; null until loaded. */
  const [liveNotices, setLiveNotices] = useState<number | null>(null);
  const [status, setStatus] = useState<ContractStatus | null>(null);
  const [automation, setAutomation] = useState<AutomationWalletStatus | null>(null);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  // Total claimable needs a full depositor scan (~3s), so it is loaded on
  // demand rather than blocking the rest of the strip on mount.
  const [claimable, setClaimable] = useState<{ total: string; eligible: number } | null>(null);
  const [loadingClaimable, setLoadingClaimable] = useState(false);
  /** True while the shared refresh is reloading every on-chain figure. */
  const [loadingFigures, setLoadingFigures] = useState(false);
  /** When the header refresh last completed, for the "Updated Nm ago" label. */
  const [lastRefresh, setLastRefresh] = useState<number | null>(null);
  /** Ticks once a minute so the relative timestamp stays honest. */
  const [, setClock] = useState(0);

  const isBusy = (name: string) => activeAction === name;
  const isLoading = activeAction !== null;

  const loadStatus = useCallback(async () => {
    // Independent so one failing read does not blank the other stats.
    try {
      setStatus(await Web3Service.getContractStatus());
    } catch (error) {
      console.error('[AdminPage] failed to load contract status', error);
    }
    try {
      setAutomation(await Web3Service.getAutomationWalletStatus());
    } catch (error) {
      console.error('[AdminPage] failed to load automation wallet', error);
      setAutomation(null);
    }
    try {
      setLiveNotices((await fetchVisibleNotices()).length);
    } catch (error) {
      console.error('[AdminPage] failed to load notices', error);
      setLiveNotices(null);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    setLastRefresh(Date.now());
  }, [loadStatus]);

  // Re-render once a minute so the relative "Updated Nm ago" label does not
  // sit frozen at "just now" while the page is left open.
  useEffect(() => {
    const t = setInterval(() => setClock((c) => c + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const loadClaimable = useCallback(async () => {
    setLoadingClaimable(true);
    try {
      setClaimable(await Web3Service.getTotalClaimable());
    } catch (error) {
      console.error('[AdminPage] failed to compute total claimable', error);
      setClaimable(null);
    } finally {
      setLoadingClaimable(false);
    }
  }, []);

  /**
   * One refresh for every figure that changes on-chain: fees accrued, CVT V2
   * and V1 held, and total claimable. Previously the glyph only recalculated
   * claimable, so an admin who withdrew fees still saw the stale balance.
   */
  const refreshFigures = useCallback(async () => {
    setLoadingFigures(true);
    try {
      await Promise.all([loadStatus(), loadClaimable()]);
      setLastRefresh(Date.now());
    } finally {
      setLoadingFigures(false);
    }
  }, [loadStatus, loadClaimable]);

  /**
   * Runs an action, recording its result against the card that triggered it
   * rather than in one shared banner far from the control.
   */
  const run = async (key: string, okText: string, fn: () => Promise<any>) => {
    setActiveAction(key);
    setResults((r) => ({ ...r, [key]: undefined }));
    try {
      await fn();
      setResults((r) => ({ ...r, [key]: { ok: true, text: okText } }));
      // Reload the fast on-chain figures after every action. Withdrawing fees
      // or CVT changes the balances shown on those same cards, so leaving them
      // stale would show an amount that is no longer there.
      loadStatus();
      // Actions that move funds also change what is claimable; only re-run the
      // expensive depositor scan for those, not for every admin action.
      if (['fees', 'cvt', 'vesting', 'unpause', 'pause'].includes(key)) {
        loadClaimable();
      }
    } catch (error: any) {
      console.error(`[AdminPage] ${key} failed`, error);
      setResults((r) => ({
        ...r,
        [key]: { ok: false, text: error?.message?.slice(0, 160) || 'Transaction failed.' },
      }));
    } finally {
      setActiveAction(null);
    }
  };

  /** Irreversible actions route through a type-to-confirm modal. */
  const guard = (c: PendingConfirm) => {
    setConfirmInput('');
    setConfirm(c);
  };

  /**
   * Reject a malformed address before opening the wallet. Without this the tx
   * is signed and then reverts on-chain, costing gas for a typo.
   */
  const withAddress = async (
    key: string,
    addr: string,
    fn: () => Promise<any>,
    okText: string
  ): Promise<void> => {
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr.trim())) {
      setResults((r) => ({
        ...r,
        [key]: { ok: false, text: 'Enter a valid address (0x followed by 40 hex characters).' },
      }));
      return;
    }
    await run(key, okText, fn);
  };

  if (showDashboard) {
    return <AdminDashboard setShowDashboard={setShowDashboard} />;
  }

  /** "just now" / "3m ago" / "2h ago" — answers "is this current?". */
  const agoLabel = (): string => {
    if (!lastRefresh) return '';
    const secs = Math.floor((Date.now() - lastRefresh) / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ago`;
  };

  const label = (key: string, idle: string, busy: string) =>
    isBusy(key) ? (
      <>
        <Spinner />
        {busy}
      </>
    ) : (
      idle
    );

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(1200px 700px at top left, rgb(2, 36, 19), rgb(0, 2, 5) 30%)',
        color: INK,
        fontFamily: "'Sora',sans-serif",
        padding: '32px 24px 96px',
      }}
    >
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ ...LABEL, color: MINT }}>CVT V1 &rarr; V2 Vesting</div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, margin: '6px 0 4px' }}>
              <h1 style={{ fontSize: 30, fontWeight: 700, margin: 0 }}>Admin console</h1>
              {/* Protocol state sits with the title: it is the first thing an
                  admin checks, and it is a status rather than a figure. */}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '5px 12px',
                  borderRadius: 999,
                  border: `1px solid ${status ? (status.paused ? 'rgba(245,180,76,0.4)' : 'rgba(52,211,153,0.35)') : 'rgba(255,255,255,0.12)'}`,
                  background: status ? (status.paused ? 'rgba(245,180,76,0.10)' : 'rgba(52,211,153,0.08)') : 'transparent',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: status ? (status.paused ? AMBER : GREEN) : MUTED,
                    boxShadow: status && !status.paused ? `0 0 8px ${GREEN}` : undefined,
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 600, color: status ? (status.paused ? AMBER : MINT) : MUTED }}>
                  {status ? (status.paused ? 'Paused' : 'Live') : '—'}
                </span>
              </span>

              {/* One refresh for the whole page. Per-cell glyphs implied a
                  scope they never had — all three ran this same handler. */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <RefreshGlyph
                  onClick={refreshFigures}
                  busy={loadingFigures}
                  label="Refresh all figures"
                />
                <span style={{ fontSize: 11, color: DIM, whiteSpace: 'nowrap' }}>
                  {loadingFigures ? 'Refreshing…' : agoLabel() && `Updated ${agoLabel()}`}
                </span>
              </span>
            </div>
            <div style={{ fontSize: 13, color: MUTED }}>Contract management interface</div>
          </div>
          <div style={{ minWidth: 0, maxWidth: 420 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <a
              href={`${EXPLORER_URL}/address/${VESTING_ADDRESS}#asset-multichain`}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 999, background: 'rgba(255,255,255,0.03)', textDecoration: 'none' }}
            >
              <span style={{ fontSize: 11, color: DIM, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Contract</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: MINT }}>
                {`${VESTING_ADDRESS.slice(0, 6)}…${VESTING_ADDRESS.slice(-4)}`}
              </span>
              <span style={{ fontSize: 12, color: MUTED }}>&#8599;</span>
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 999, background: 'rgba(255,255,255,0.03)' }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
              <span style={{ fontSize: 11, color: DIM, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Owner</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: SOFT }}>
                {account ? `${account.slice(0, 6)}…${account.slice(-4)}` : '—'}
              </span>
            </div>
            </div>
            <CopyableAddress address={VESTING_ADDRESS} />
          </div>
        </div>

        {/* ── Live status strip ──────────────────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', marginTop: 20 }}>
          {/* Protocol status moved up beside the page title — it is the one
              thing an admin checks first, and it is a state not a figure.
              Initial unlock / weekly vest live inside the vesting card. */}
          {[
            { k: 'Fees accrued', v: status ? `${truncateDecimals(status.feesCollected, 3)} BNB` : '—' },
            {
              k: 'CVT V2 in contract',
              v: status
                ? status.v2Balance !== null
                  ? formatWithCommas(status.v2Balance, 2)
                  : 'unavailable'
                : '—',
              title: status?.v2Balance ? `${status.v2Balance} CVT V2` : undefined,
            },
          ].map((s) => (
            <div key={s.k} style={{ flex: '1 1 170px', background: '#0A1310', padding: '14px 16px' }}>
              <div style={{ ...LABEL, fontSize: 10 }}>{s.k}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
                <span style={{ fontSize: 17, fontWeight: 600 }} title={s.title}>
                  {s.v}
                </span>
              </div>
            </div>
          ))}
          {/* Total claimable: on-demand because it scans every depositor. */}
          <div style={{ flex: '1 1 210px', background: '#0A1310', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ ...LABEL, fontSize: 10 }}>Total claimable</div>
              {/* The header slot is the entry point to the user list, matching
                  "Manage →" on the neighbouring cells. */}
              <button onClick={() => setShowDashboard(true)} style={chipBtn()}>
                Users &rarr;
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span
                style={{ fontSize: 17, fontWeight: 600 }}
                title={claimable ? `${claimable.total} CVT` : undefined}
              >
                {claimable ? formatWithCommas(claimable.total, 2) : '—'}
              </span>
              <RefreshGlyph
                onClick={refreshFigures}
                busy={loadingFigures || loadingClaimable}
                label={claimable ? 'Recalculate total claimable' : 'Calculate — scans every depositor'}
              />
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 5 }}>
              {claimable
                ? `${claimable.eligible}/${status?.depositorCount ?? '—'} eligible`
                : `${status?.depositorCount ?? '—'} depositors`}
            </div>
          </div>

          {/* Automation wallet. Replaces the Chainlink upkeep cell: automation
              now runs from an off-chain bot that pays its own BNB gas, so the
              thing to watch is that wallet's balance. */}
          <div style={{ flex: '1 1 220px', background: '#0A1310', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ ...LABEL, fontSize: 10 }}>Automation wallet</div>
              <button onClick={() => setShowAutomation(true)} style={chipBtn()}>
                Manage &rarr;
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: automation ? (automation.isLow ? RED : GREEN) : MUTED,
                }}
              />
              <span
                style={{ fontSize: 17, fontWeight: 600, color: automation?.isLow ? RED : INK }}
                title={automation ? `${automation.balance} BNB` : undefined}
              >
                {automation ? `${truncateDecimals(automation.balance, 4)} BNB` : '—'}
              </span>
            </div>
            <div style={{ fontSize: 11, color: automation?.isLow ? RED : MUTED, marginTop: 4 }}>
              {automation
                ? automation.isLow
                  ? 'Needs a top-up'
                  : 'Funded'
                : '—'}
            </div>
          </div>
          {/* Site notices — opens a centred modal with full CRUD. */}
          <div style={{ flex: '1 1 200px', background: '#0A1310', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ ...LABEL, fontSize: 10 }}>Site notices</div>
              <button onClick={() => setShowNotices(true)} style={chipBtn()}>
                Manage &rarr;
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: liveNotices === null ? MUTED : liveNotices > 0 ? AMBER : GREEN,
                }}
              />
              <span style={{ fontSize: 17, fontWeight: 600, color: liveNotices ? AMBER : INK }}>
                {liveNotices === null ? '—' : liveNotices > 0 ? `${liveNotices} showing` : 'None'}
              </span>
            </div>
          </div>

        </div>

        {/* ── Routine ────────────────────────────────────────────── */}
        <SectionHeading title="Routine operations" note="Reversible. Take effect immediately." />
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))' }}>
          {/* Global vesting */}
          <div style={CARD}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={GREEN}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8v4l3 3" strokeLinecap="round" /><circle cx="12" cy="12" r="9" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Global vesting schedule</div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              Applies to every future claim. Current values shown in the fields.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ ...LABEL, marginBottom: 5 }}>Initial %</div>
                <input
                  style={INPUT}
                  value={globalInitialPercent}
                  placeholder={status?.initialPercent ?? '—'}
                  onChange={(e) => setGlobalInitialPercent(e.target.value)}
                />
              </div>
              <div>
                <div style={{ ...LABEL, marginBottom: 5 }}>Weekly %</div>
                <input
                  style={INPUT}
                  value={globalMinutelyPercent}
                  placeholder={status?.weeklyPercent ?? '—'}
                  onChange={(e) => setGlobalMinutelyPercent(e.target.value)}
                />
              </div>
            </div>
            {status && (globalInitialPercent || globalMinutelyPercent) && (
              <div style={{ fontSize: 11, color: AMBER }}>
                Replacing {status.initialPercent}% / {status.weeklyPercent}% with{' '}
                {globalInitialPercent || status.initialPercent}% / {globalMinutelyPercent || status.weeklyPercent}%
              </div>
            )}
            <button
              disabled={isLoading || !globalInitialPercent.trim() || !globalMinutelyPercent.trim()}
              style={btnPrimary(isLoading || !globalInitialPercent.trim() || !globalMinutelyPercent.trim())}
              onClick={() =>
                run('vesting', 'Global vesting parameters updated.', () =>
                  Web3Service.setGlobalVestingParams(globalInitialPercent, globalMinutelyPercent)
                )
              }
            >
              {label('vesting', 'Update schedule', 'Updating…')}
            </button>
            <Result result={results.vesting} />
          </div>

          {/* User access */}
          <div style={CARD}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={GREEN}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M5 5l14 14" strokeLinecap="round" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>User access</div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              A blocked user cannot claim or deposit. Unblocking restores access.
            </div>
            <div>
              <div style={{ ...LABEL, marginBottom: 5 }}>User address</div>
              <input style={INPUT} placeholder="0x…" value={user} onChange={(e) => setUser(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                disabled={isLoading}
                style={btnOutline(isLoading, 'rgba(240,97,107,0.35)', RED)}
                onClick={() => withAddress('block', user, () => Web3Service.blockUser(user), 'User blocked.')}
              >
                {label('block', 'Block', 'Blocking…')}
              </button>
              <button
                disabled={isLoading}
                style={btnPrimary(isLoading)}
                onClick={() => withAddress('unblock', user, () => Web3Service.unblockUser(user), 'User unblocked.')}
              >
                {label('unblock', 'Unblock', 'Unblocking…')}
              </button>
            </div>
            <Result result={results.block || results.unblock} />
          </div>

          {/* Admin management */}
          <div style={CARD}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={GREEN}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l7 4v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V7z" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Admin management</div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              Admins can run every action on this page except ownership transfer.
            </div>
            <div>
              <div style={{ ...LABEL, marginBottom: 5 }}>Admin address</div>
              <input style={INPUT} placeholder="0x…" value={adminAddress} onChange={(e) => setAdminAddress(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                disabled={isLoading}
                style={btnPrimary(isLoading)}
                onClick={() => withAddress('addAdmin', adminAddress, () => Web3Service.addAdmin(adminAddress), 'Admin added.')}
              >
                {label('addAdmin', 'Add admin', 'Adding…')}
              </button>
              <button
                disabled={isLoading}
                style={btnOutline(isLoading, 'rgba(240,97,107,0.35)', RED)}
                onClick={() =>
                  guard({
                    title: 'Remove admin',
                    body: `${adminAddress || 'This address'} will lose all admin permissions on this contract.`,
                    word: 'REMOVE',
                    cta: 'Remove admin',
                    run: () => withAddress('removeAdmin', adminAddress, () => Web3Service.removeAdmin(adminAddress), 'Admin removed.'),
                  })
                }
              >
                {label('removeAdmin', 'Remove admin', 'Removing…')}
              </button>
            </div>
            <Result result={results.addAdmin || results.removeAdmin} />
          </div>

          {/* Emergency claim — hidden. Uncomment to restore.
          <div style={CARD}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={AMBER}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L4 14h7l-1 8 9-12h-7z" strokeLinejoin="round" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Emergency claim</div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              Forces a claim on behalf of a user who cannot transact themselves. Funds go to the user.
            </div>
            <div>
              <div style={{ ...LABEL, marginBottom: 5 }}>User address</div>
              <input style={INPUT} placeholder="0x…" value={emergencyClaimUser} onChange={(e) => setEmergencyClaimUser(e.target.value)} />
            </div>
            <button
              disabled={isLoading}
              style={btnOutline(isLoading, 'rgba(245,180,76,0.35)', AMBER)}
              onClick={() => run('emergency', 'Emergency claim triggered.', () => Web3Service.emergencyTriggerClaim(emergencyClaimUser))}
            >
              {label('emergency', 'Trigger claim', 'Triggering…')}
            </button>
            <Result result={results.emergency} />
          </div>
          */}
        </div>

        {/* ── Treasury ───────────────────────────────────────────── */}
        <SectionHeading title="Treasury" note="Moves funds out of the contract. Confirmation required." tone={AMBER} />
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))' }}>
          <div style={CARD}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={AMBER}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="7" width="18" height="12" rx="2" /><path d="M16 13h.01" strokeLinecap="round" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Withdraw fees</div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>Sends the full accumulated fee balance to the owner address.</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 12px', background: 'rgba(0,0,0,0.35)', borderRadius: 9 }}>
              <span style={{ ...LABEL, fontSize: 10 }}>Available</span>
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {status ? `${truncateDecimals(status.feesCollected, 4)} BNB` : '—'}
              </span>
            </div>
            <button
              disabled={isLoading}
              style={btnOutline(isLoading, 'rgba(245,180,76,0.35)', AMBER)}
              onClick={() =>
                guard({
                  title: 'Withdraw fees',
                  body: `The full fee balance${status ? ` (${truncateDecimals(status.feesCollected, 4)} BNB)` : ''} will be sent to the owner address.`,
                  word: 'WITHDRAW',
                  cta: 'Withdraw fees',
                  run: () => run('fees', 'Fees withdrawn.', () => Web3Service.withdrawFees()),
                })
              }
            >
              {label('fees', 'Withdraw fees', 'Withdrawing…')}
            </button>
            <Result result={results.fees} />
          </div>

          {/* Withdraw CVT — treasury movement, but irreversible, so it keeps
              the red card treatment and a type-to-confirm gate. */}
          <div style={CARD_DANGER}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={EMBER}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 7l1 13h12l1-13M9 7V4h6v3M4 7h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Withdraw CVT</div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              Sends every V1 CVT deposited by users to the owner.
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 12px', background: 'rgba(0,0,0,0.3)', borderRadius: 9 }}>
              <span style={{ ...LABEL, fontSize: 10 }}>Held</span>
              <span
                style={{ fontSize: 16, fontWeight: 600 }}
                title={status?.v1Balance ? `${status.v1Balance} CVT` : undefined}
              >
                {status
                  ? status.v1Balance !== null
                    ? `${formatWithCommas(status.v1Balance, 2)} CVT`
                    : 'unavailable'
                  : '—'}
              </span>
            </div>
            <button
              disabled={isLoading}
              style={btnOutline(isLoading, 'rgba(224,138,92,0.4)', EMBER)}
              onClick={() =>
                guard({
                  title: 'Withdraw all CVT',
                  body: `Every V1 CVT token deposited by users${
                    status?.v1Balance ? ` (${formatWithCommas(status.v1Balance, 2)} CVT)` : ''
                  } will be transferred to the owner address. This cannot be undone.`,
                  word: 'WITHDRAW CVT',
                  cta: 'Withdraw CVT',
                  run: () => run('cvt', 'CVT withdrawn.', () => Web3Service.withdrawOldTokens()),
                })
              }
            >
              {label('cvt', 'Withdraw CVT', 'Withdrawing…')}
            </button>
            <Result result={results.cvt} />
          </div>
        </div>

        {/* ── Irreversible ───────────────────────────────────────── */}
        {/* Not "Irreversible" — pausing can be undone with Unpause. The risk
            here is blast radius, not permanence. */}
        <SectionHeading title="Protocol control" note="Affects every user at once. Confirmation required." tone={EMBER} />
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))' }}>
          <div style={CARD_DANGER}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <IconBox tone={EMBER}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M10 9v6M14 9v6" strokeLinecap="round" /></svg>
              </IconBox>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                Contract state {status && <span style={{ color: status.paused ? AMBER : MINT }}>— {status.paused ? 'Paused' : 'Live'}</span>}
              </div>
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              Pausing halts claims, deposits and swaps for all users until it is manually unpaused.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                disabled={isLoading || status?.paused}
                style={btnOutline(isLoading || !!status?.paused, 'rgba(224,138,92,0.4)', EMBER)}
                onClick={() =>
                  guard({
                    title: 'Pause contract',
                    body: 'All claims, deposits and swaps will stop for every user until you manually unpause.',
                    word: 'PAUSE',
                    cta: 'Pause contract',
                    run: () => run('pause', 'Contract paused.', () => Web3Service.pauseContract()),
                  })
                }
              >
                {label('pause', 'Pause', 'Pausing…')}
              </button>
              <button
                disabled={isLoading || (status !== null && !status.paused)}
                style={btnPrimary(isLoading || (status !== null && !status.paused))}
                onClick={() => run('unpause', 'Contract unpaused.', () => Web3Service.unpauseContract())}
              >
                {label('unpause', 'Unpause', 'Unpausing…')}
              </button>
            </div>
            <Result result={results.pause || results.unpause} />
          </div>
        </div>
      </div>

      {/* ── Site notices (centred modal) ─────────────────────────── */}
      <NoticePanel
        open={showNotices}
        onClose={() => {
          setShowNotices(false);
          loadStatus();
        }}
      />

      {/* ── Automation wallet (centred modal) ────────────────────── */}
      <AutomationWalletPanel
        open={showAutomation}
        onClose={() => {
          setShowAutomation(false);
          // A top-up changes the balance shown in the strip.
          loadStatus();
        }}
      />

      {/* ── Type-to-confirm modal ────────────────────────────────── */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 60 }}>
          <div style={{ width: 'min(440px,100%)', background: '#070C09', border: '1px solid rgba(240,97,107,0.3)', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ ...LABEL, color: RED }}>Irreversible action</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{confirm.title}</div>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{confirm.body}</div>
            <div style={{ fontSize: 12, color: SOFT }}>
              Type <strong style={{ color: RED, fontFamily: "'JetBrains Mono',monospace" }}>{confirm.word}</strong> to continue
            </div>
            <input
              autoFocus
              style={{ ...INPUT, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.14)', padding: '10px 12px' }}
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirm(null)} style={{ ...btnOutline(false), padding: '10px 18px' }}>
                Cancel
              </button>
              <button
                disabled={confirmInput !== confirm.word}
                style={{
                  ...BTN_BASE,
                  border: 0,
                  padding: '10px 18px',
                  background: 'linear-gradient(180deg, #E8505B, #C23A45)',
                  color: '#fff',
                  opacity: confirmInput === confirm.word ? 1 : 0.4,
                  cursor: confirmInput === confirm.word ? 'pointer' : 'not-allowed',
                }}
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  c.run();
                }}
              >
                {confirm.cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
